import React, { useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import successIcon from '../../../assets/imgs/icons/check-circle-fill-green.svg';
import infoIcon from '../../../assets/imgs/icons/info-circle-fill-blue.svg';
import errorIcon from '../../../assets/imgs/icons/warning-circle-fill-red.svg';

import './CommonToast.scss';

let _toastRef: Toast | null = null;

const TOAST_ICONS = {
  info: infoIcon,
  warn: infoIcon,
  error: errorIcon,
  success: successIcon,
};

export const showCommonToast = (message: string, severity: 'info' | 'warn' | 'success' | 'error' = 'warn') => {
  if (!_toastRef || !message) return;
  _toastRef.show({
    severity,
    detail: message,
    life: 5000,
    closable: false,
  });
};

// Helper functions for different severity levels
export const showInfoToast = (message: string) => showCommonToast(message, 'info');
export const showWarnToast = (message: string) => showCommonToast(message, 'warn');
export const showSuccessToast = (message: string) => showCommonToast(message, 'success');
export const showErrorToast = (message: string) => showCommonToast(message, 'error');

interface CustomToastContentProps {
  message: any;
  toastRef: React.RefObject<Toast | null>;
}

const CustomToastContent: React.FC<CustomToastContentProps> = ({ message, toastRef }) => {
  const severity = (message?.severity || 'info') as 'info' | 'warn' | 'success' | 'error';
  const icon = TOAST_ICONS[severity];

  useEffect(() => {
    if (!message?.closable) {
      const timer = setTimeout(() => {
        if (toastRef.current && message) {
          toastRef.current.remove(message);
        }
      }, message?.life || 5000);
      return () => clearTimeout(timer);
    }
  }, [message, toastRef]);

  const handleClose = () => {
    if (toastRef.current && message) {
      toastRef.current.remove(message);
    }
  };

  return (
    <>
      {message && (
        <div className="custom-toast-content mb-2">
          <div className="custom-toast-icon">
            <img src={icon} alt={severity} width="28" height="28" />
          </div>
          <div className="custom-toast-message">{message?.detail ?? ''}</div>
          <button
            className="custom-toast-close"
            onClick={handleClose}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label="Close"
            type="button"
          >
            <i className="pi pi-times"></i>
          </button>
        </div>
      )}
    </>
  );
};

const CommonToast: React.FC = () => {
  const ref = useRef<Toast>(null);

  useEffect(() => {
    _toastRef = ref.current;

    return () => {
      if (_toastRef === ref.current) _toastRef = null;
    };
  }, []);

  const customContent = (props: any) => {
    return <CustomToastContent message={props.message} toastRef={ref} />;
  };

  return <Toast ref={ref} position="top-right" content={customContent} className="custom-toast" />;
};

export default CommonToast;
